import { useState } from 'react'
import { useLocale, type SupportedLocale } from '@/lib/LocaleContext'
import { LANGUAGES } from '@/lib/languages'
import { sdk } from '@/lib/sdk'
import { useAuth } from '@/lib/AuthContext'
import { Button } from '../ui/button'
import { Globe, Check } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'


export const LanguageSelector = () => {
  const { currentLocale, changeLanguage } = useLocale()
  const { isAuthenticated } = useAuth()
  const [isChanging, setIsChanging] = useState(false)

  const currentLanguage = LANGUAGES.find(lang => lang.code === currentLocale) || LANGUAGES[0]

  const handleLanguageChange = async (locale: SupportedLocale) => {
    setIsChanging(true)
    try {
      await changeLanguage(locale)

      /*
       * Picking here also saves it to the account.
       *
       * Otherwise the choice lives in this browser's localStorage only: the
       * same person on their phone gets another language, and every email
       * still goes out in the community's. The screen does not wait for it —
       * the interface has already switched, and a slow request must not make
       * the menu feel stuck.
       */
      if (isAuthenticated) {
        sdk.members.updateMe({ language: locale }).catch(() => {
          /* The preference is not lost: it stays in this browser and can be
             saved again from the account screen. */
        })
      }
    } catch (error) {
      console.error('LanguageSelector: failed to change language:', error)
    } finally {
      setIsChanging(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center gap-2 rounded-md hover:bg-gray-100"
          disabled={isChanging}
        >
          <Globe className="h-4 w-4" />
          <span>{currentLanguage.short}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {LANGUAGES.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{language.flag}</span>
              <div className="flex flex-col">
                <span className="font-medium">{language.country}</span>
                <span className="text-xs text-gray-500">{language.language}</span>
              </div>
            </div>
            {currentLocale === language.code && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}